import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { PartLoading } from "../../../../../../react-core/Html/PartLoading";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { OrderDirection } from "../../../../../../Wrap/Constants/Enums/Domain/OrderDirection";
import { UserClaim } from "../../../../../../Wrap/Constants/Enums/UserClaim";
import { BatchInfoModel } from "../../../../../../Wrap/Models/Domains/Orders/Batch/BatchInfoModel";
import { Order } from "../../../../../../Wrap/Models/Domains/Orders/Order";
import { Permission } from "../../../../../Shared/Permission/Permission";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { ITableConfig, Table } from "../../../../../Shared/Table/Table";
import { ITypographySectionProps, Typography } from "../../../../../Shared/Typography/Typography";
import { getQueryParameters } from "../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { batchTypeArraysMap } from "../../Common/Resource/Contents";
import { CancelOrderAction } from "../Action/Action";

interface IReviewProps extends IProcessComponentProps {}

interface IReviewState {
    batchInfo: BatchInfoModel;
    isLoading: boolean;
    isConfirmed: boolean;
    isConfiming: boolean;
}

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = {
            batchInfo: null,
            isLoading: true,
            isConfirmed: false,
            isConfiming: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.getData(false);
    }

    private getData = (isConfirmed) => {
        this.setState({ isLoading: true });
        const params = getQueryParameters();
        CancelOrderAction.getBatchInfo(
            {
                BatchId: params["batchid"],
                TransId: this.props.data.batchInfo.TransId,
                ClAccountId: params["claccountid"],
                IsConfirmed: isConfirmed,
            },
            (data) => {
                this.setState({ batchInfo: data, isLoading: false, isConfirmed: isConfirmed });
            }
        );
    };

    private getBatchTypographyConfig(data: BatchInfoModel): ITypographySectionProps[] {
        const params = getQueryParameters();
        return [
            { title: "Tax Wrapper", data: params["claccountid"] },
            { title: "Batch ID", data: data.BatchId },
            { title: "Batch type", data: batchTypeArraysMap[data.BatchType] },
        ];
    }

    private getTableConfig(orderType: OrderDirection): ITableConfig[] {
        const tableColConfig: ITableConfig[] = [
            {
                title: "Investment",
                key: "Description",
                render: (data, row: Order, index) => {
                    return row.Description;
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: `${orderType == OrderDirection.Buy ? "Buy" : "Sell"} Value`,
                key: "OrderValue",
                render: (data, row: Order, index) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(row.OrderValue, CurrencyCode.GBP, true, 2);
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: `${orderType == OrderDirection.Buy ? "Buy" : "Sell"} Quantity`,
                key: "OrderQuantity",
                render: (data, row: Order, index) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(row.OrderQuantity, null, false, 4);
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },

            {
                title: "Limit Price",
                key: "OrderLimit",
                render: (data, row: Order, index) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(row.OrderLimit, null, false, 2);
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: "Status",
                key: "OrderStatus",
                render: (data, row: Order, index) => {
                    return row.OrderStatus;
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
        ]; // all table column config

        return tableColConfig;
    }

    public render(): JSX.Element {
        const { batchInfo, isLoading } = this.state;
        if (isLoading) {return <PartLoading isLoading={isLoading} />;}
        const buyList = batchInfo.InvestmentList.filter((ele) => ele.BuyOrSell == OrderDirection.Buy);
        const buyCancelledTotal = buyList
            .filter((ele) => ele.OrderStatus.toLowerCase() == "cancelled")
            .reduce((total, ele) => {
                return total + ele.OrderValue;
            }, 0);
        const buyTotal = buyList.reduce((total, ele) => {
            return total + ele.OrderValue;
        }, 0);

        const sellList = batchInfo.InvestmentList.filter((ele) => ele.BuyOrSell == OrderDirection.Sell);
        const sellCancelledTotal = sellList
            .filter((ele) => ele.OrderStatus.toLowerCase() == "cancelled")
            .reduce((total, ele) => {
                return total + ele.OrderValue;
            }, 0);
        const sellTotal = sellList.reduce((total, ele) => {
            return total + ele.OrderValue;
        }, 0);

        return (
            <>
                <Typography type="common" config={this.getBatchTypographyConfig(batchInfo)} />
                <hr />
                {buyList && buyList.length > 0 && (
                    <Table
                        col={this.getTableConfig(OrderDirection.Buy)}
                        data={buyList}
                        TotalBody={[
                            { data: "Estimated total buy trade value", colSpan: 1 },
                            {
                                data: MoneyDisplayHelper.FromatMoneyDisplay(
                                    buyTotal - buyCancelledTotal,
                                    CurrencyCode.GBP,
                                    true,
                                    2
                                ),
                                tfTdCssWrapper: "cell-left",
                                colSpan: 4,
                            },
                        ]}
                        SubBody={[
                            { data: "Estimated Net order value", colSpan: 1 },
                            {
                                data: MoneyDisplayHelper.FromatMoneyDisplay(
                                    buyCancelledTotal - buyTotal,
                                    CurrencyCode.GBP,
                                    true,
                                    2
                                ),
                                tfTdCssWrapper: "cell-left",
                                colSpan: 4,
                            },
                        ]}
                    />
                )}
                <br />
                {sellList && sellList.length > 0 && (
                    <Table
                        col={this.getTableConfig(OrderDirection.Sell)}
                        data={sellList}
                        TotalBody={[
                            { data: "Estimated total sell trade value", colSpan: 1 },
                            {
                                data: MoneyDisplayHelper.FromatMoneyDisplay(
                                    sellTotal - sellCancelledTotal,
                                    CurrencyCode.GBP,
                                    true,
                                    2
                                ),
                                tfTdCssWrapper: "cell-left",
                                colSpan: 4,
                            },
                        ]}
                        SubBody={[
                            { data: "Estimated Net Order Value", colSpan: 1 },
                            {
                                data: MoneyDisplayHelper.FromatMoneyDisplay(
                                    sellCancelledTotal - sellTotal,
                                    CurrencyCode.GBP,
                                    true,
                                    2
                                ),
                                tfTdCssWrapper: "cell-left",
                                colSpan: 4,
                            },
                        ]}
                    />
                )}
                {this.state.isConfirmed ? (
                    <Permission userClaim={UserClaim.AdviserPortalDluOrdersCanView}>
                        <button
                            className="btn primary"
                            onClick={() =>
                                (window.location.href = `/clients/orders/orders.aspx?claccountid=${
                                    getQueryParameters()["claccountid"]
                                }`)
                            }
                        >
                            Go to Transaction
                        </button>
                    </Permission>
                ) : (
                    <button
                        className={`btn primary ${this.state.isConfiming ? "is-busy" : ""}`}
                        onClick={() => {
                            this.setState({ isConfiming: true });
                            CancelOrderAction.submit(this.state.batchInfo, (result) => {
                                if (!result.SubmissionSucceed) {
                                    this.props.showErrorMsgPopUp([
                                        `There was an error when processing cancel Batch ${result.BatchId}.`,
                                    ]);
                                } else {
                                    this.getData(true);
                                }
                            });
                        }}
                    >
                        Confirm
                    </button>
                )}
            </>
        );
    }
}
